import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  Modal,
  Radio,
  Space,
  Table,
  Tooltip,
  App as AntApp,
} from "antd";
import {
  DownloadOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import type { TableRowSelection } from "antd/es/table/interface";
import { useSearchParams } from "react-router-dom";

import { ordersApi } from "../api/orders";
import type { Order, OrderItem, OrderStatus } from "../api/types";
import { Avatar } from "../components/Avatar";
import { PageHeader } from "../components/PageHeader";
import { Pill } from "../components/Pill";
import { formatDateTimeIST, formatINR, formatRelative } from "../utils/format";

const STATUS_TABS: { label: string; value: OrderStatus | "all" }[] = [
  { label: "All", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Accepted", value: "accepted" },
  { label: "Rejected", value: "rejected" },
];

export function Orders() {
  const { notification, modal } = AntApp.useApp();
  const [status, setStatus] = useState<OrderStatus | "all">("pending");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rows, setRows] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<React.Key[]>([]);
  const [reviewing, setReviewing] = useState<Order | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page, page_size: pageSize };
      if (status !== "all") params.status = status;
      if (search) params.search = search;
      const res = await ordersApi.list(params);
      setRows(res.results);
      setTotal(res.count);
    } finally {
      setLoading(false);
    }
  }, [status, search, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const openReview = (order: Order) => {
    setReviewing(order);
    setRejectionReason("");
  };

  // Deep link from the dashboard: /orders?review=<id> opens that order's review modal.
  useEffect(() => {
    const id = searchParams.get("review");
    if (!id) return;
    let cancelled = false;
    ordersApi
      .get(id)
      .then((order) => {
        if (cancelled) return;
        setReviewing(order);
        setRejectionReason("");
      })
      .catch(() => {
        if (!cancelled) notification.error({ message: "Order not found" });
      })
      .finally(() => {
        if (!cancelled) {
          const next = new URLSearchParams(searchParams);
          next.delete("review");
          setSearchParams(next, { replace: true });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams, notification]);

  const submitReview = async (decision: "accept" | "reject", overrideCredit = false) => {
    if (!reviewing) return;
    if (decision === "reject" && !rejectionReason.trim()) {
      notification.warning({ message: "Reason is required to reject" });
      return;
    }
    try {
      await ordersApi.review(reviewing.id, {
        decision,
        rejection_reason: decision === "reject" ? rejectionReason : undefined,
        override_credit: overrideCredit || undefined,
      });
      notification.success({
        message: `Order ${reviewing.order_number} ${decision === "accept" ? "accepted" : "rejected"}`,
      });
      setReviewing(null);
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string; code?: string } } };
      const data = err.response?.data;
      // Offer admins an override when the only problem is the credit limit.
      if (decision === "accept" && data?.code === "credit_limit_exceeded") {
        modal.confirm({
          title: "Credit limit exceeded",
          content: `${data.detail} Accept anyway and override the credit limit?`,
          okText: "Override & Accept",
          okButtonProps: { danger: true },
          onOk: () => submitReview("accept", true),
        });
        return;
      }
      notification.error({ message: data?.detail || "Review failed" });
    }
  };

  const bulkReview = (decision: "accept" | "reject") => {
    if (selected.length === 0) return;
    modal.confirm({
      title: `${decision === "accept" ? "Accept" : "Reject"} ${selected.length} orders?`,
      content:
        decision === "reject"
          ? "All selected orders will be rejected with a generic reason. Use single review for per-order reasons."
          : "All selected pending orders will be accepted.",
      okButtonProps: { danger: decision === "reject" },
      onOk: async () => {
        const res = await ordersApi.bulkReview({
          order_ids: selected.map(String),
          decision,
          rejection_reason: decision === "reject" ? "Bulk reject by admin" : undefined,
        });
        setSelected([]);
        const skippedStock = res.skipped_out_of_stock?.length || 0;
        const skippedCredit = res.skipped_credit_exceeded?.length || 0;
        const parts = [`${res.updated} ${decision}ed`];
        if (skippedStock) parts.push(`${skippedStock} skipped (out of stock)`);
        if (skippedCredit) parts.push(`${skippedCredit} skipped (credit limit)`);
        notification.success({ message: `Bulk ${decision} complete`, description: parts.join(" · ") });
        load();
      },
    });
  };

  const rowSelection: TableRowSelection<Order> = {
    selectedRowKeys: selected,
    onChange: setSelected,
    getCheckboxProps: (row) => ({ disabled: row.status !== "pending" }),
  };

  const columns = useMemo(
    () => [
      {
        title: "Order #",
        dataIndex: "order_number",
        render: (v: string) => <span className="mono" style={{ fontSize: 12 }}>{v}</span>,
      },
      {
        title: "Employee",
        dataIndex: "employee_name",
        render: (v: string) => (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Avatar name={v} size={24} />
            {v}
          </div>
        ),
      },
      { title: "Shop", dataIndex: "shop_name" },
      {
        title: "Items",
        align: "right" as const,
        render: (_: unknown, row: Order) => row.items.length,
      },
      {
        title: "Amount",
        dataIndex: "total_amount",
        align: "right" as const,
        render: (v: string) => <span className="tabular">{formatINR(v)}</span>,
      },
      {
        title: "Submitted",
        dataIndex: "submitted_at",
        render: (v: string) => (
          <Tooltip title={formatDateTimeIST(v)}>
            <span className="small-muted">{formatRelative(v)}</span>
          </Tooltip>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        render: (v: OrderStatus) => <Pill kind={v} />,
      },
      {
        title: "",
        render: (_: unknown, row: Order) => (
          <Button type="primary" size="small" onClick={() => openReview(row)}>
            {row.status === "pending" ? "Review" : "View"}
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <div className="page-shell">
      <PageHeader
        title="Incoming Orders"
        subtitle="Review and accept or reject orders submitted by your employees"
      />

      <div className="toolbar-row">
        <Radio.Group
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          optionType="button"
        >
          {STATUS_TABS.map((t) => (
            <Radio.Button key={t.value} value={t.value}>
              {t.label}
            </Radio.Button>
          ))}
        </Radio.Group>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search order # or shop…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <div style={{ marginLeft: "auto" }}>
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => {
                const params: Record<string, string> = {};
                if (status !== "all") params.status = status;
                window.open(ordersApi.exportUrl(params), "_blank");
              }}
            >
              Export CSV
            </Button>
            <Button icon={<ReloadOutlined />} onClick={load} />
          </Space>
        </div>
      </div>

      {selected.length > 0 && (
        <div className="bulk-bar">
          <div style={{ fontWeight: 500, color: "#1E40AF" }}>{selected.length} orders selected</div>
          <Space>
            <Button type="primary" onClick={() => bulkReview("accept")}>
              Accept All Selected
            </Button>
            <Button danger onClick={() => bulkReview("reject")}>
              Reject All Selected
            </Button>
          </Space>
        </div>
      )}

      <Table
        rowKey="id"
        size="middle"
        loading={loading}
        columns={columns}
        dataSource={rows}
        rowSelection={rowSelection}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />

      <Modal
        open={!!reviewing}
        onCancel={() => setReviewing(null)}
        title={
          reviewing ? (
            <span>
              Review Order <span className="mono">{reviewing.order_number}</span>
            </span>
          ) : (
            ""
          )
        }
        width={900}
        footer={[
          <Button key="cancel" onClick={() => setReviewing(null)}>
            Cancel
          </Button>,
          <Button
            key="reject"
            danger
            disabled={reviewing?.status !== "pending"}
            onClick={() => submitReview("reject")}
          >
            Reject
          </Button>,
          <Button
            key="accept"
            type="primary"
            disabled={reviewing?.status !== "pending"}
            onClick={() => submitReview("accept")}
          >
            Accept
          </Button>,
        ]}
      >
        {reviewing && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div>
              <div className="kpi-label" style={{ marginBottom: 8 }}>Order Details</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
                <Avatar name={reviewing.employee_name} size={32} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{reviewing.employee_name}</div>
                  <div className="small-muted">{reviewing.employee_email}</div>
                </div>
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.7 }}>
                <div>
                  <span style={{ color: "var(--text-muted)" }}>Shop:</span> {reviewing.shop_name}
                </div>
                <div>
                  <span style={{ color: "var(--text-muted)" }}>Address:</span> {reviewing.shop_address}
                </div>
                <div>
                  <span style={{ color: "var(--text-muted)" }}>Submitted:</span>{" "}
                  {formatRelative(reviewing.submitted_at)}
                </div>
                <div className="small-muted">{formatDateTimeIST(reviewing.submitted_at)}</div>
                <div style={{ marginTop: 4 }}>
                  <span style={{ color: "var(--text-muted)" }}>Status:</span>{" "}
                  <Pill kind={reviewing.status} />
                </div>
                {reviewing.rejection_reason && (
                  <div style={{ marginTop: 4 }}>
                    <span style={{ color: "var(--text-muted)" }}>Reason:</span> {reviewing.rejection_reason}
                  </div>
                )}
              </div>
              {reviewing.notes && (
                <div
                  style={{
                    marginTop: 12,
                    padding: 10,
                    background: "var(--bg-subtle)",
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                >
                  <div className="small-muted" style={{ marginBottom: 4 }}>Employee notes</div>
                  <div style={{ fontStyle: "italic" }}>"{reviewing.notes}"</div>
                </div>
              )}
            </div>
            <div>
              <div className="kpi-label" style={{ marginBottom: 8 }}>Items</div>
              <Table
                size="small"
                pagination={false}
                rowKey="id"
                dataSource={reviewing.items}
                columns={[
                  {
                    title: "Product",
                    dataIndex: "product_name",
                    render: (v: string, row: OrderItem) => (
                      <>
                        {v}
                        {row.colour && (
                          <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                            {row.colour}
                          </div>
                        )}
                      </>
                    ),
                  },
                  { title: "Qty", dataIndex: "quantity", align: "right" as const },
                  {
                    title: "Price",
                    dataIndex: "unit_price",
                    align: "right" as const,
                    render: (v: string, row: OrderItem) => {
                      const markup = Number(row.markup_percent ?? 0);
                      return (
                        <span className="tabular">
                          {formatINR(v)}
                          {markup > 0 && (
                            <span
                              style={{
                                marginLeft: 6,
                                fontSize: 11,
                                color: "var(--text-secondary)",
                              }}
                            >
                              +{markup}%
                            </span>
                          )}
                        </span>
                      );
                    },
                  },
                  {
                    title: "Subtotal",
                    dataIndex: "subtotal",
                    align: "right" as const,
                    render: (v: string) => <span className="tabular">{formatINR(v)}</span>,
                  },
                ]}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3} align="right">
                      <b>Total</b>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <b>{formatINR(reviewing.total_amount)}</b>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
              <div style={{ marginTop: 12 }}>
                <div className="kpi-label" style={{ marginBottom: 6 }}>Reason (if rejecting)</div>
                <Input.TextArea
                  rows={3}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Explain why this order is being rejected…"
                  disabled={reviewing.status !== "pending"}
                />
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
